/**
 * EFormSettingsModal — corp's "Create New Survey" and "Edit E-Form" dialogs,
 * for one account, in corp's field order:
 *   - Add: Title, Page Title, Form Type, Header / Submission Message (rich
 *     text), Clickable Link Shortcode and its URL shortcode, Submission URL,
 *     the three colours, Active / Contact Me / Display Logo, the lead source,
 *     the e-mail and SMS templates;
 *   - Edit: the same minus Active and Contact Me (corp offers them only on
 *     create), plus the Direct Link, Legacy Direct Link and Embed Code with
 *     copy / open buttons.
 * Left out: "Allow Download" (nothing stores it), "Autoresponder" (corp's
 * picker had no choices; the stored one is kept) and "Expires In" (link
 * expiry is undecided; a new form saves 0, never expires, and Edit keeps the
 * stored value). The lead source saves a level-2 source, as legacy (corp saved
 * a category id when it had no sources).
 *
 * On a Locked page AdminFormModal makes it a viewer (plan 2026-10-10-001, U7);
 * the bespoke pieces here follow its field lock: the colour swatches and the
 * shortcode take no change and the message pencils are gone.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { Input } from '@/components/ui/Input';
import { SearchableCombobox } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { cn } from '@/lib/utils';
import { getErrorMessage } from '@/services/apiClient';
import {
  FORM_TYPES,
  createEForm,
  getEFormLinks,
  listLeadSourceTree,
  newEFormSettings,
  settingsFrom,
  updateEForm,
  type EForm,
  type EFormSettings,
  type LeadSourceNode,
} from '@/services/eformsAdmin.service';
import { listEmailTemplateOptions, listSmsTemplateOptions, type TaskOption } from '@/services/tasksAdmin.service';
import { toast } from '@/store/toastStore';
import { AdminFormModal } from './AdminFormModal';
import { useAdminPageLevel } from './AdminPageLevelContext';
import { DESCRIPTION_CLASS, DialogField, fieldClasses } from './DialogChrome';
import { HtmlPreviewField } from './RichTextDialog';
import { useTrackedForm } from './useTrackedForm';

const NONE = 0;

function ColorField({ id, label, value, placeholder, onChange, badge }: { id: string; label: string; value: string; placeholder: string; onChange: (v: string) => void; badge?: ReactNode }) {
  const swatch = /^#[0-9a-fA-F]{6}$/.test(value) ? value : placeholder;
  const locked = useFieldLocked();
  return (
    <DialogField label={label} htmlFor={id} badge={badge}>
      <div className="flex items-center gap-2">
        {locked ? (
          // The colour itself, with no picker behind it.
          <span aria-hidden className="h-9 w-12 shrink-0 rounded border border-slate-200 bg-white p-0.5">
            <span className="block h-full w-full rounded-sm" style={{ backgroundColor: swatch }} />
          </span>
        ) : (
          <input
            type="color"
            aria-label={`${label} swatch`}
            value={swatch}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            className="h-9 w-12 shrink-0 cursor-pointer rounded border border-slate-200 bg-white p-0.5"
          />
        )}
        <Input id={id} value={value} placeholder={placeholder} maxLength={20} onChange={(e) => onChange(e.target.value)} />
      </div>
    </DialogField>
  );
}

function LinkField({ id, label, value, copyLabel, openLabel, copiedToast }: { id: string; label: string; value: string; copyLabel: string; openLabel: string; copiedToast: string }) {
  return (
    <DialogField label={label} htmlFor={id}>
      <div className="flex items-center gap-2">
        <Input id={id} readOnly value={value} placeholder="Generated after save" className="bg-slate-50 text-slate-600" />
        {value && (
          <>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              leftIcon={<Copy className="h-4 w-4" />}
              aria-label={copyLabel}
              title={copyLabel}
              onClick={() => void navigator.clipboard?.writeText(value).then(() => toast.success(copiedToast))}
            />
            <a
              href={value}
              target="_blank"
              rel="noreferrer"
              aria-label={openLabel}
              title={openLabel}
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-slate-600 hover:bg-slate-100"
            >
              <ExternalLink className="h-4 w-4" aria-hidden />
            </a>
          </>
        )}
      </div>
    </DialogField>
  );
}

export interface EFormSettingsModalProps {
  /** Null to add one. */
  form: EForm | null;
  onClose: () => void;
  /** After a save: the form's id, and the settings as saved. */
  onSaved: (id: number, isNew: boolean, settings: EFormSettings) => void;
}

export function EFormSettingsModal({ form: eform, onClose, onSaved }: EFormSettingsModalProps) {
  const isNew = eform === null;
  // The page's level: this component sits outside the modal's own field lock.
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm<EFormSettings>(() => (eform ? settingsFrom(eform) : newEFormSettings()), { tracking: !isNew });
  const { form } = t;
  const [saving, setSaving] = useState(false);
  const [leadSources, setLeadSources] = useState<LeadSourceNode[]>([]);
  const [emailTemplates, setEmailTemplates] = useState<TaskOption[] | null>(null);
  const [smsTemplates, setSmsTemplates] = useState<TaskOption[] | null>(null);
  const [links, setLinks] = useState({
    directLink: eform?.directLink ?? '',
    legacyDirectLink: eform?.legacyDirectLink ?? '',
    embedCode: eform?.embedCode ?? '',
  });

  useEffect(() => {
    let cancelled = false;
    void listLeadSourceTree()
      .then((tree) => !cancelled && setLeadSources(tree))
      .catch(() => undefined);
    void listEmailTemplateOptions()
      .then((o) => !cancelled && setEmailTemplates(o))
      .catch(() => !cancelled && setEmailTemplates([]));
    void listSmsTemplateOptions()
      .then((o) => !cancelled && setSmsTemplates(o))
      .catch(() => !cancelled && setSmsTemplates([]));
    // Fresh links for a live form (a deleted one has none to load).
    if (eform?.active) {
      void getEFormLinks(eform.id)
        .then((l) => !cancelled && setLinks((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(l).filter(([, v]) => v)) })))
        .catch(() => undefined);
    }
    return () => {
      cancelled = true;
    };
  }, [eform]);

  // ── Lead source: a category picks which sources are offered; the form stores the source.
  const activeCategories = useMemo(() => leadSources.filter((c) => c.active), [leadSources]);
  const storedCategory = useMemo(() => {
    if (!form.leadSourceId) return null;
    return (
      leadSources.find((c) => c.children.some((s) => s.id === form.leadSourceId)) ??
      // A category id (corp saved one for a category without sources).
      leadSources.find((c) => c.id === form.leadSourceId) ??
      null
    );
  }, [leadSources, form.leadSourceId]);
  const [categoryId, setCategoryId] = useState<number | null>(null);
  const shownCategoryId = categoryId ?? storedCategory?.id ?? NONE;
  const category = leadSources.find((c) => c.id === shownCategoryId) ?? null;
  const sourceOptions = (category?.children ?? []).filter((s) => s.active || s.id === form.leadSourceId);
  const sourceIsChild = category?.children.some((s) => s.id === form.leadSourceId) ?? false;

  const categoryOptions = [
    { value: NONE, label: '-- None --' },
    ...activeCategories.map((c) => ({ value: c.id, label: c.name })),
    ...(storedCategory && !storedCategory.active ? [{ value: storedCategory.id, label: `${storedCategory.name} (inactive)` }] : []),
  ];

  const templateOptions = (list: TaskOption[] | null, current: number) => [
    { value: NONE, label: '-- None --' },
    ...[...(list ?? [])].filter((o) => o.active || o.id === current).sort((a, b) => a.id - b.id).map((o) => ({ value: o.id, label: o.label })),
  ];

  const missingTitle = !form.title.trim();
  const errors = missingTitle ? [isNew ? 'Survey title is required' : 'Form name is required'] : [];

  async function handleSave() {
    if (errors.length > 0 || saving) return;
    setSaving(true);
    try {
      if (eform) {
        await updateEForm(eform.id, form);
        toast.success('E-form updated successfully');
        onSaved(eform.id, false, form);
      } else {
        const id = await createEForm(form);
        toast.success('E-form created');
        onSaved(id, true, form);
      }
    } catch (err) {
      toast.error(getErrorMessage(err, isNew ? 'Creation failed' : 'Failed to update the e-form'));
    } finally {
      setSaving(false);
    }
  }

  const titleField = (
    <DialogField
      label={isNew ? 'Survey Title' : 'Form Name'}
      htmlFor="eform-title"
      required
      state={missingTitle ? 'error' : 'none'}
      labelHint={missingTitle ? '— required' : undefined}
      badge={t.badge('title', 'Form Name')}
    >
      <Input
        id="eform-title"
        value={form.title}
        maxLength={255}
        placeholder="e.g., Patient Intake Form"
        onChange={(e) => t.patch({ title: e.target.value })}
        className={fieldClasses.input(missingTitle ? 'error' : 'none')}
      />
    </DialogField>
  );

  const formTypeField = (
    <DialogField label="Form Type" htmlFor="eform-type" badge={t.badge('surveyType', 'Form Type')}>
      <Select<number>
        id="eform-type"
        options={[{ value: 0, label: '-- Select Form Type --' }, ...FORM_TYPES]}
        value={form.surveyType}
        onChange={(v) => t.patch({ surveyType: v ?? 0 })}
      />
    </DialogField>
  );

  const pageTitleField = (
    <DialogField label="Page Title" htmlFor="eform-page-title" badge={t.badge('pageTitle', 'Page Title')}>
      <Input id="eform-page-title" value={form.pageTitle} maxLength={255} placeholder="Browser tab title" onChange={(e) => t.patch({ pageTitle: e.target.value })} />
    </DialogField>
  );

  const messages = (
    <>
      <DialogField label="Header Message" badge={t.badge('headerMessage', 'Header Message')}>
        <HtmlPreviewField
          html={form.headerMessage}
          empty="No header message"
          editLabel="Edit header message"
          dialogTitle="Edit Header Message"
          onChange={(html) => t.patch({ headerMessage: html })}
        />
      </DialogField>
      <DialogField label="Submission Message" badge={t.badge('submissionMessage', 'Submission Message')}>
        <HtmlPreviewField
          html={form.submissionMessage}
          empty="No submission message"
          editLabel="Edit submission message"
          dialogTitle="Edit Submission Message"
          onChange={(html) => t.patch({ submissionMessage: html })}
        />
      </DialogField>
    </>
  );

  const shortcodeFields = (
    <div className="grid gap-4 sm:grid-cols-2">
      <DialogField label="Clickable Link Shortcode" htmlFor="eform-shortcode" badge={t.badge('shortcode', 'Clickable Link Shortcode')}>
        <div className="flex items-center rounded-md border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-blue-500">
          <span className="pl-3 font-mono text-sm text-slate-400">{'${'}</span>
          <input
            id="eform-shortcode"
            value={form.shortcode}
            maxLength={50}
            placeholder="e.g., referralForm"
            readOnly={locked}
            onChange={(e) => {
              if (!locked) t.patch({ shortcode: e.target.value });
            }}
            className="h-9 min-w-0 flex-1 bg-transparent px-1 font-mono text-sm text-slate-900 outline-none"
          />
          <span className="pr-3 font-mono text-sm text-slate-400">{'}'}</span>
        </div>
      </DialogField>
      <DialogField label="URL Shortcode">
        <div className="flex h-9 items-center rounded-md border border-slate-200 bg-slate-50 px-3 font-mono text-sm text-slate-600">
          {form.shortcode.trim() ? `\${${form.shortcode.trim()}Url}` : '—'}
        </div>
      </DialogField>
    </div>
  );

  const submissionUrlField = (
    <DialogField label="Submission URL" htmlFor="eform-submission-url" badge={t.badge('submissionUrl', 'Submission URL')}>
      <Input
        id="eform-submission-url"
        type="url"
        value={form.submissionUrl}
        placeholder="https://example.com/thank-you"
        onChange={(e) => t.patch({ submissionUrl: e.target.value })}
      />
    </DialogField>
  );

  const colors = (
    <div className="grid gap-4 sm:grid-cols-3">
      <ColorField id="eform-color1" label={isNew ? 'Primary Color (color1)' : 'Primary Color'} value={form.color1} placeholder="#FFFFFF" onChange={(v) => t.patch({ color1: v })} badge={t.badge('color1', 'Primary Color')} />
      <ColorField id="eform-color2" label={isNew ? 'Secondary Color (color2)' : 'Secondary Color'} value={form.color2} placeholder="#000000" onChange={(v) => t.patch({ color2: v })} badge={t.badge('color2', 'Secondary Color')} />
      <ColorField id="eform-font-color" label="Font Color" value={form.fontColor} placeholder="#333333" onChange={(v) => t.patch({ fontColor: v })} badge={t.badge('fontColor', 'Font Color')} />
    </div>
  );

  const leadSourceFields = (
    <div className="grid gap-4 sm:grid-cols-2">
      <DialogField label={isNew ? 'Lead Source (Level 1)' : 'Lead Source Category'} htmlFor="eform-lead-category">
        <Select<number>
          id="eform-lead-category"
          options={categoryOptions}
          value={shownCategoryId}
          onChange={(v) => {
            setCategoryId(v ?? NONE);
            t.patch({ leadSourceId: 0 });
          }}
        />
      </DialogField>
      <DialogField
        label={isNew ? 'Lead Source (Level 2)' : 'Lead Source'}
        htmlFor="eform-lead-source"
        badge={t.badge('leadSourceId', 'Lead Source')}
        description={category && category.children.length === 0 ? 'This category has no lead sources.' : undefined}
      >
        <Select<number>
          id="eform-lead-source"
          options={[{ value: NONE, label: '-- None --' }, ...sourceOptions.map((s) => ({ value: s.id, label: s.name }))]}
          value={sourceIsChild ? form.leadSourceId : NONE}
          onChange={(v) => t.patch({ leadSourceId: v ?? 0 })}
          disabled={!category || sourceOptions.length === 0}
          placeholder={category ? 'Select lead source...' : 'Select a category first'}
        />
      </DialogField>
    </div>
  );

  const templateFields = (
    <>
      <DialogField label="Email Template" htmlFor="eform-email-template" badge={t.badge('emailContentId', 'Email Template')}>
        <SearchableCombobox<number>
          id="eform-email-template"
          options={templateOptions(emailTemplates, form.emailContentId)}
          value={form.emailContentId}
          onChange={(v) => t.patch({ emailContentId: v ?? 0 })}
          placeholder="Select email template"
          emptyMessage="No email templates found."
          loading={emailTemplates === null}
        />
      </DialogField>
      <DialogField label="SMS Template" htmlFor="eform-sms-template" badge={t.badge('smsId', 'SMS Template')}>
        <SearchableCombobox<number>
          id="eform-sms-template"
          options={templateOptions(smsTemplates, form.smsId)}
          value={form.smsId}
          onChange={(v) => t.patch({ smsId: v ?? 0 })}
          placeholder="Select SMS template"
          emptyMessage="No SMS templates found."
          loading={smsTemplates === null}
        />
      </DialogField>
    </>
  );

  return (
    <AdminFormModal
      title={isNew ? 'Add E-Form' : 'Edit E-Form'}
      description={
        isNew ? 'Create a new electronic form. Its questions are added in the Form Builder, which opens next.' : locked ? undefined : 'Update e-form information'
      }
      isNew={isNew}
      dirty={t.dirty}
      saving={saving}
      errors={errors}
      saveLabel={isNew ? 'Create' : 'Save Changes'}
      onSave={() => void handleSave()}
      onRevertAll={() => {
        t.revertAll();
        setCategoryId(null);
      }}
      onClose={onClose}
      size="xl"
    >
      <div className="grid gap-4">
        {isNew ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {titleField}
              {pageTitleField}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">{formTypeField}</div>
            {messages}
            {shortcodeFields}
            {submissionUrlField}
            {colors}
            <div className="flex flex-wrap gap-6">
              <Checkbox id="eform-active" label="Active" checked={form.active} onChange={(e) => t.patch({ active: e.target.checked })} />
              <Checkbox id="eform-contact-me" label="Contact Me" checked={form.contactMe} onChange={(e) => t.patch({ contactMe: e.target.checked })} />
              <Checkbox id="eform-display-logo" label="Display Logo" checked={form.displayLogo} onChange={(e) => t.patch({ displayLogo: e.target.checked })} />
            </div>
            {!form.active && <p className={cn(DESCRIPTION_CLASS, 'text-amber-600')}>An inactive form is created as deleted: it shows under Show deleted.</p>}
            {leadSourceFields}
            {templateFields}
          </>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              {titleField}
              {formTypeField}
            </div>
            {pageTitleField}
            {messages}
            {submissionUrlField}
            {templateFields}
            {shortcodeFields}
            <div className="flex items-center gap-3">
              <Switch id="eform-display-logo" checked={form.displayLogo} onChange={(v) => t.patch({ displayLogo: v })} aria-label="Display Logo" />
              <label htmlFor="eform-display-logo" className="text-sm text-slate-900">
                Display Logo
              </label>
              {t.badge('displayLogo', 'Display Logo')}
            </div>
            <LinkField id="eform-direct-link" label="Direct Link" value={links.directLink} copyLabel="Copy direct link" openLabel="Open form in new tab" copiedToast="Direct link copied to clipboard" />
            <LinkField
              id="eform-legacy-link"
              label="Legacy Direct Link"
              value={links.legacyDirectLink}
              copyLabel="Copy legacy direct link"
              openLabel="Open legacy form in new tab"
              copiedToast="Legacy direct link copied to clipboard"
            />
            <DialogField label="Embed Code" htmlFor="eform-embed">
              <div className="flex items-start gap-2">
                <Textarea id="eform-embed" readOnly rows={3} value={links.embedCode} placeholder="Generated after save" className="resize-none bg-slate-50 font-mono text-xs text-slate-600" />
                {links.embedCode && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconOnly
                    leftIcon={<Copy className="h-4 w-4" />}
                    aria-label="Copy embed code"
                    title="Copy embed code"
                    onClick={() => void navigator.clipboard?.writeText(links.embedCode).then(() => toast.success('Embed code copied to clipboard'))}
                  />
                )}
              </div>
            </DialogField>
            {colors}
            {leadSourceFields}
          </>
        )}
      </div>
    </AdminFormModal>
  );
}
